import React from 'react';
import { Dialog } from '@mui/material';
import { Wallet } from '../../../../api';
import { Button, CARD_LOGO } from '../../../../components';
import { assetUrl } from '../../../../utils/webConfig';

type PreferredCardDeleteConfirmationModalProps = {
    open: boolean;
    wallet: Wallet;
    otherWallets: Wallet[];
    isUpdating?: boolean;
    onConfirm: (newPreferred: Wallet) => void;
    onCancel: () => void;
};

export const PreferredCardDeleteConfirmationModal: React.FC<
    PreferredCardDeleteConfirmationModalProps
> = ({ open, wallet, otherWallets, isUpdating, onConfirm, onCancel }) => {
    const [selectedId, setSelectedId] = React.useState<number | null>(null);

    const lastFour = wallet.number.slice(-4);

    const handleConfirm = () => {
        const selected = otherWallets.find((w) => w.id === selectedId);
        if (selected) onConfirm(selected);
    };

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (!isUpdating) onCancel();
            }}
            container={document.getElementById('root')}
            sx={{
                '& .MuiBackdrop-root': { backgroundColor: '#f1f0f7cc' },
                '& .MuiDialog-paper': {
                    borderRadius: '24px',
                    padding: '40px 24px',
                    backgroundColor: '#ffffff',
                    boxShadow:
                        '0 1px 6px 0 rgba(10, 0, 77, 0.04), 0 5.5px 30px 0 rgba(10, 0, 77, 0.05)',
                    minWidth: 320,
                    maxWidth: 400,
                    mx: { xs: 2, sm: 'auto' },
                },
            }}
        >
            <div
                data-testid="preferred-delete-content"
                className="flex flex-col"
            >
                <div
                    data-testid="preferred-delete-title"
                    className="text-heading-small text-primary mb-6 text-center"
                >
                    Select a Preferred Card
                </div>
                <div
                    data-testid="preferred-delete-description"
                    className="flex flex-col gap-4 mb-6"
                >
                    <div
                        data-testid="preferred-delete-warning"
                        className="text-body-medium text-primary"
                    >
                        We will delete your card ending in{' '}
                        <strong>{lastFour}.</strong> Any recurring gifts
                        associated with this card will be cancelled.
                    </div>
                    <div
                        data-testid="preferred-delete-instruction"
                        className="text-body-medium text-primary"
                    >
                        At least one card must be set as preferred. Please
                        select from your other cards below.
                    </div>
                </div>
                <div
                    data-testid="preferred-delete-options"
                    className="flex flex-col"
                >
                    {otherWallets.map((w) => {
                        const type = w.type.toLowerCase();
                        const logoSrc = CARD_LOGO[type];
                        const cardLastFour = w.number.slice(-4);
                        return (
                            <label
                                key={w.id}
                                data-testid={`preferred-card-option-${w.id}`}
                                className="flex items-center gap-4 py-3 border-b border-[#ececec] cursor-pointer"
                            >
                                <input
                                    data-testid={`preferred-card-radio-${w.id}`}
                                    type="radio"
                                    name="preferred-card"
                                    value={w.id}
                                    checked={selectedId === w.id}
                                    onChange={() => setSelectedId(w.id)}
                                    className="accent-guiding-violet w-4 h-4 shrink-0"
                                />
                                {logoSrc && (
                                    <img
                                        data-testid={`preferred-card-logo-${w.id}`}
                                        src={assetUrl(logoSrc)}
                                        alt={w.type}
                                        className="h-4 w-[50px] object-contain"
                                    />
                                )}
                                <span
                                    data-testid={`preferred-card-last-four-${w.id}`}
                                    className="text-body-medium text-primary"
                                >
                                    •••• {cardLastFour}
                                </span>
                            </label>
                        );
                    })}
                </div>
                <div
                    data-testid="preferred-delete-actions"
                    className="flex flex-col items-center gap-4 mt-6"
                >
                    <Button
                        data-testid="preferred-delete-select-button"
                        className="w-full"
                        disabled={selectedId === null || isUpdating}
                        loading={isUpdating}
                        onClick={handleConfirm}
                    >
                        Select
                    </Button>
                    <Button
                        data-testid="preferred-delete-cancel-button"
                        variant="link"
                        disabled={isUpdating}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};
